import { Suspense, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAssistantScope } from '@rammetec/app-kit/assistant'
import {
  logActivity,
  useAvailableModules,
  useModuleInstance,
  useModulesResolving,
} from '@syscolect/application'
import type { ModuleInstance } from '@syscolect/domain'
import { GenericModulePage } from '@/shared/components/GenericModulePage'
import { RouteFallback } from '@/shared/components/RouteFallback'
import { ForbiddenPage } from '@/shared/components/ForbiddenPage'
import { InstancePicker } from './InstancePicker'
import { GroupLandingPage } from './GroupLandingPage'
import { slugToComponent, type AppModule } from './registry'
import { CurrentModuleProvider } from './CurrentModuleContext'
import { useInstanceDisplayMap } from './InstanceDisplayContext'
import { getPersistedMid } from './modulePreferences'

/**
 * Deterministic page-scope anchor for assistant navigation (request 0291).
 * Must render inside `<Suspense>` next to the lazy page so it mounts in the
 * same commit as the grid tools — otherwise `awaitScope` resolves too early.
 */
function ModuleAssistantScope({
  instance,
  title,
}: {
  instance: ModuleInstance
  title: string
}) {
  useAssistantScope({ id: `module:${instance.id}`, title })
  return null
}

/** Emit `module.open` once per mounted instance (request 0352). */
function ModuleOpenActivity({
  instance,
  label,
}: {
  instance: ModuleInstance
  label: string
}) {
  useEffect(() => {
    logActivity({
      action: 'module.open',
      module: instance.slug,
      summary: `Abriu módulo ${label}`,
      attrs: { instanceId: instance.id },
    })
  }, [instance.id, instance.slug, label])
  return null
}

/**
 * Single entry point for `/m/:slug`. Resolves access + instance via
 * `useModuleInstance`, then dispatches:
 *  - `kind === 'group'` → GroupLandingPage (children grid + breadcrumbs)
 *  - leaf slug in slugToComponent → mapped page component (lazy, own chunk)
 *  - unmapped leaf → GenericModulePage
 *  - no access → ForbiddenPage
 *  - multiple candidates, no valid mid → InstancePicker
 */
export function ModuleEntry() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const urlMid = searchParams.get('mid')
  const mid = urlMid ?? (slug ? getPersistedMid(slug) : null)

  const { instance, candidates, needsDisambiguation } = useModuleInstance(slug, mid)
  const modules = useAvailableModules<AppModule>()
  const instanceDisplay = useInstanceDisplayMap()
  const isResolving = useModulesResolving()

  if (!slug) return <ForbiddenPage />

  // No instances — could be transient (still fetching) or a genuine lack of access.
  // Only fall to ForbiddenPage once resolution has settled.
  if (candidates.length === 0) {
    if (isResolving) return <RouteFallback />
    return <ForbiddenPage slug={slug} />
  }

  if (needsDisambiguation) return <InstancePicker slug={slug} candidates={candidates} />

  if (!instance) return <ForbiddenPage slug={slug} />

  const moduleDef = modules.find((m) => m.slug === instance.slug)

  if (moduleDef?.kind === 'group') {
    // Groups share AppModule slug 'g' — override iconNode with the per-instance icon.
    const iconNode = instanceDisplay.get(instance.id)?.icon ?? moduleDef.iconNode
    const moduleWithIcon: AppModule = { ...moduleDef, iconNode }
    const groupLabel = instance.label ?? moduleDef.label ?? instance.slug
    return (
      <CurrentModuleProvider value={moduleWithIcon}>
        <ModuleOpenActivity instance={instance} label={groupLabel} />
        <GroupLandingPage instance={instance} />
      </CurrentModuleProvider>
    )
  }

  // Mapped pages are lazy (one chunk per module) — the fallback covers the
  // fetch of that chunk. `GenericModulePage` is eager but harmless inside it.
  const Component = slugToComponent[slug] ?? GenericModulePage
  const scopeTitle = instance.label ?? moduleDef?.label ?? instance.slug
  return (
    <CurrentModuleProvider value={moduleDef ?? null}>
      <ModuleOpenActivity instance={instance} label={scopeTitle} />
      <Suspense fallback={<RouteFallback />}>
        <ModuleAssistantScope instance={instance} title={scopeTitle} />
        <Component instance={instance} />
      </Suspense>
    </CurrentModuleProvider>
  )
}
